import { apiUnifiedProfiles } from './apiClient.js';
import {
  FORBIDDEN_TYPE_IDS,
  bumpMockProfileTypeCount,
  getMockProfileTypes,
} from './__mocks__/profileTypes.mock.js';
import { MOCK_NOW_ISO, appendMockEntity, getMockEntities } from './__mocks__/entities.mock.js';
import { appendMockEntityHeader, getMockEntityHeaders } from './__mocks__/entityHeader.mock.js';
import {
  MOCK_CITIES,
  MOCK_INDUSTRIES,
  MOCK_LOCATIONS,
  MOCK_OWNERS,
} from './__mocks__/recordForm.mock.js';
import {
  getMockDashboardLayouts,
  getMockDashboardLists,
} from './__mocks__/dashboards.mock.js';
import { buildMockWidgetData } from './__mocks__/widgetData.mock.js';
import { getMockProfileTabs } from './__mocks__/profileTabs.mock.js';
import { getMockTabData } from './__mocks__/profileTabData.mock.js';
import {
  ACTIVITY_FAILING_ENTITY_IDS,
  activityFilterDefsFor,
  hasMockActivity,
  queryMockActivity,
  toApiActivityRow,
} from './__mocks__/activity.mock.js';
import {
  addMockComment,
  createMockNote,
  getMockDrive,
  getMockNoteDetail,
  listMockComments,
  updateMockNote,
} from './__mocks__/notes.mock.js';
import {
  getMockInsight,
  getMockTrait,
  queryMockInsights,
  queryMockTraits,
  reportMockInsight,
  reportMockTrait,
} from './__mocks__/intelligence.mock.js';
import { getMockHowToApproach } from './__mocks__/approach.mock.js';
import { createMockDriveFolder, listMockEntityDrive, setMockDriveExtraction } from './__mocks__/entityDrive.mock.js';
import {
  getMockTruthFact,
  getMockTruthSummary,
  listMockTruthFacts,
  reportMockTruthFact,
  requestMockFactChange,
} from './__mocks__/truth.mock.js';
import {
  confirmMockClaim,
  getMockConflict,
  listMockSandboxGroups,
  resolveMockConflict,
  sendMockClaimForConfirmation,
} from './__mocks__/sandbox.mock.js';
import { listMockNotesFeed, logMockInteraction } from './__mocks__/history.mock.js';
import { listMockTasks, runMockTaskMutation } from './__mocks__/tasks.mock.js';
import {
  getMockActiveWorkflow,
  listMockNextBestActions,
  startMockNextBestActionWorkflow,
} from './__mocks__/nextBestAction.mock.js';

/** Default on until the entity endpoints land (CT-5277 / backend). */
export const USE_MOCKS = import.meta.env.VITE_UNIFIED_PROFILES_USE_MOCKS !== 'false';

/**
 * Long enough that loading states and race guards are actually exercisable.
 * A zero-latency mock makes every spinner untestable and hides every race.
 */
const MOCK_LATENCY_MS = 300;
const ENV_FORCE_ERROR = import.meta.env.VITE_UNIFIED_PROFILES_MOCK_FORCE_ERROR === 'true';

/** Test / QA override — set via `__setMockForceFailure` or the env flag above. */
let forceFailureOverride = null;

export const __setMockForceFailure = (value) => {
  forceFailureOverride = value;
};

export const __resetMockForceFailure = () => {
  forceFailureOverride = null;
};

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const envelope = (body, meta = {}) => ({ status_code: 200, body, meta });

const errorEnvelope = (statusCode, description) => ({
  status_code: statusCode,
  body: null,
  msg_code: [{ code: 'error', description }],
  meta: {},
});

/**
 * 403 and 404 are separate envelopes on purpose.
 *
 * The screens render different things for them — "you cannot see this" versus
 * "this does not exist" — and collapsing them here would make that distinction
 * untestable until the real backend arrived. CT-5275 and CT-5276 both depend on
 * this staying separable.
 */
const forbidden = (entityType) =>
  errorEnvelope(403, `No access to the ${entityType} Unified Profile`);
const notFound = (description) => errorEnvelope(404, description);

const handleTypeList = (params = {}) => {
  const {
    page = 1,
    page_size: pageSize = 12,
    order_by: orderBy = 'label',
    order_direction: orderDirection = 'asc',
    search = {},
  } = params;

  // Searches the LABEL, not the slug: the label is what the user reads on the
  // card, so matching the slug would make a query that looks right return
  // nothing.
  const needle = (search.query ?? '').trim().toLowerCase();
  const matched = needle
    ? getMockProfileTypes().filter((type) => (type.label ?? '').toLowerCase().includes(needle))
    : getMockProfileTypes();

  const sorted = [...matched].sort((a, b) => {
    const result =
      orderBy === 'label' ? (a.label ?? '').localeCompare(b.label ?? '', undefined, { numeric: true }) : 0;
    return orderDirection === 'desc' ? -result : result;
  });

  const totalItems = sorted.length;
  const start = (page - 1) * pageSize;
  const items = sorted.slice(start, start + pageSize);
  const totalPages = pageSize > 0 ? Math.max(1, Math.ceil(totalItems / pageSize)) : 1;

  return envelope(
    { items, page, page_size: pageSize, total: totalItems },
    {
      total_items: totalItems,
      total_pages: totalPages,
      current_page: page,
      per_page: pageSize,
      has_next: start + items.length < totalItems,
      has_previous: page > 1,
    },
  );
};

/**
 * What a row can be narrowed by, per type — DATA, not code.
 *
 * A Dealership and a Vehicle share no fields, so the dimensions travel with the
 * rows and the frontend only holds what the user selected. Adding one is a
 * seed change here (an endpoint change later), never a release.
 *
 * Ids are resolved against a row by convention: `status` reads the first badge,
 * anything else reads the `meta` entry with the matching label. A real endpoint
 * would index these; the mock just has to answer the same shape.
 */
const FILTER_DEFS = {
  customers: [
    {
      id: 'status',
      label: 'Status',
      options: [
        { value: 'Active', label: 'Active' },
        { value: 'At risk', label: 'At risk' },
        { value: 'Overdue', label: 'Overdue' },
      ],
    },
    {
      id: 'region',
      label: 'Region',
      options: [
        { value: 'AMER', label: 'AMER' },
        { value: 'EMEA', label: 'EMEA' },
      ],
    },
  ],
  employees: [
    { id: 'status', label: 'Status', options: [{ value: 'Full time', label: 'Full time' }] },
  ],
  companies: [
    {
      id: 'status',
      label: 'Status',
      options: [
        { value: 'Active', label: 'Active' },
        { value: 'Prospect', label: 'Prospect' },
      ],
    },
  ],
  vehicles: [],
  dealerships: [],
};

/** Every type sorts by the one field the normalized contract guarantees. */
/**
 * The sorts every type shares. The All view can offer only these: a sort that
 * exists on one type (`status`) means nothing across a Customer and a Vehicle.
 */
const COMMON_SORT_DEFS = [
  { value: 'last_interaction', label: 'Last interaction' },
  { value: 'title', label: 'Name' },
];

const SORT_DEFS = {
  customers: [...COMMON_SORT_DEFS, { value: 'status', label: 'Status' }],
  employees: COMMON_SORT_DEFS,
  companies: [...COMMON_SORT_DEFS, { value: 'status', label: 'Status' }],
  vehicles: COMMON_SORT_DEFS,
  dealerships: COMMON_SORT_DEFS,
};

const titleOf = (row) => row.title ?? row.name ?? row.label ?? '';

/** Read one filter dimension off a row without knowing the entity type. */
const facetOf = (row, defId) => {
  if (defId === 'status') return row.badges?.[0]?.label ?? '';
  const entry = (row.meta ?? []).find((m) => m.label?.toLowerCase() === defId.toLowerCase());
  return entry?.value ?? '';
};

/** A dimension with no selection does not narrow; several selections are OR'd. */
const matchesFilters = (row, filters = {}) =>
  Object.entries(filters).every(([defId, values]) => {
    const selected = Array.isArray(values) ? values : [values].filter(Boolean);
    if (selected.length === 0) return true;
    return selected.includes(facetOf(row, defId));
  });

/**
 * The one filter every type shares. Its options are read off the records
 * themselves, not listed: each type brings its own status words, and a fixed
 * list would be wrong the day a type adds one.
 */
const allViewStatusFilter = (rows) => {
  const values = [...new Set(rows.map((row) => facetOf(row, 'status')).filter(Boolean))].sort();
  return { id: 'status', label: 'Status', options: values.map((value) => ({ value, label: value })) };
};

const lastInteractionOf = (row) => row.last_interaction ?? row.lastInteraction ?? '';

/** A row says which type it belongs to; seed rows that predate that get it here. */
const withEntityType = (row, typeId) =>
  row.entity_type ?? row.entityType ? row : { ...row, entity_type: typeId };

/**
 * `entity_type` is OPTIONAL: omitted means the All view — every type the caller
 * may see, merged. A forbidden type simply has no rows to contribute, so it
 * leaves the merged list without failing it; asking for it directly still 403s.
 */
const handleEntityList = (params = {}) => {
  const {
    entity_type: entityType,
    page = 1,
    page_size: pageSize = 10,
    order_by: orderBy = 'last_interaction',
    order_direction: orderDirection = 'desc',
    filters = {},
    search = {},
  } = params;

  const isAll = !entityType;
  if (!isAll && FORBIDDEN_TYPE_IDS.includes(entityType)) return forbidden(entityType);

  const seed = getMockEntities();
  let rows;
  if (isAll) {
    rows = Object.entries(seed).flatMap(([typeId, list]) =>
      list.map((row) => withEntityType(row, typeId)),
    );
  } else {
    if (!seed[entityType]) return notFound(`Unknown Unified Profile type ${entityType}`);
    rows = seed[entityType].map((row) => withEntityType(row, entityType));
  }

  const needle = (search.query ?? '').trim().toLowerCase();
  const fields = search.fields?.length ? search.fields : ['title'];
  const haystack = (row) =>
    fields
      .map((field) => (field === 'title' ? titleOf(row) : (row[field] ?? '')))
      .join(' ')
      .toLowerCase();

  // Filters belong to one type (their ids and options differ), so the All view
  // keeps only the one dimension every row has — its status — and ignores the
  // rest rather than matching nothing.
  const applicableFilters = isAll ? { status: filters.status } : filters;
  const matched = rows
    .filter((row) => (needle ? haystack(row).includes(needle) : true))
    .filter((row) => matchesFilters(row, applicableFilters));

  // A sort the type does not know falls back to the title instead of throwing:
  // the page can carry `status` over from Customers into the All view.
  const sortValue = (row) => {
    if (orderBy === 'last_interaction') return lastInteractionOf(row);
    if (orderBy === 'title' || isAll) return titleOf(row);
    return facetOf(row, orderBy);
  };
  const sorted = [...matched].sort((a, b) => {
    const byValue = sortValue(a).localeCompare(sortValue(b), undefined, { numeric: true });
    const result = byValue || titleOf(a).localeCompare(titleOf(b), undefined, { numeric: true });
    return orderDirection === 'desc' ? -result : result;
  });

  const totalItems = sorted.length;
  const start = (page - 1) * pageSize;
  const items = sorted.slice(start, start + pageSize);
  const totalPages = pageSize > 0 ? Math.max(1, Math.ceil(totalItems / pageSize)) : 1;

  return envelope(
    {
      items,
      page,
      page_size: pageSize,
      total: totalItems,
      filters: isAll ? [allViewStatusFilter(rows)] : (FILTER_DEFS[entityType] ?? []),
      sorts: isAll ? COMMON_SORT_DEFS : (SORT_DEFS[entityType] ?? []),
    },
    {
      total_items: totalItems,
      total_pages: totalPages,
      current_page: page,
      per_page: pageSize,
      has_next: start + items.length < totalItems,
      has_previous: page > 1,
    },
  );
};

const handleEntityGet = (params = {}) => {
  const { entity_type: entityType, entity_id: entityId } = params;

  if (FORBIDDEN_TYPE_IDS.includes(entityType)) return forbidden(entityType);

  const rows = getMockEntityHeaders()[entityType];
  if (!rows) return notFound(`Unknown Unified Profile type ${entityType}`);

  // Reads through the same alias chain the mapper uses — the seed stores a
  // different id key per type, and matching on one spelling would make every
  // camelCase type look like a 404.
  const idOf = (row) => row.entity_id ?? row.entityId ?? row.id;
  const row = rows.find((candidate) => idOf(candidate) === entityId);

  if (!row) return notFound(`No ${entityType} with id ${entityId}`);
  // A workflow started from a Next Best Action shows on the header (CT-5713).
  const activeWorkflow = getMockActiveWorkflow(entityType, entityId);
  return envelope(activeWorkflow ? { ...row, active_workflow: activeWorkflow } : row);
};

const handleDashboardList = (params = {}) => {
  const { scope_type: scopeType, module } = params;

  if (FORBIDDEN_TYPE_IDS.includes(scopeType)) return forbidden(scopeType);

  // Scoped by entity type AND module: a tab's module is what picks which
  // dashboards it shows, so Garage and Overview on the same vehicle are
  // different sets. Ignoring it would make every tab render Overview's.
  //
  // Nothing published for a (type, module) pair is an EMPTY LIST, not a 404 —
  // the tab exists, nobody has published behind it yet, and the tab renders its
  // empty state rather than an error.
  const list = getMockDashboardLists()[scopeType]?.[module] ?? [];
  return envelope({ items: list }, { total_items: list.length });
};

const handleDashboardLayout = (params = {}) => {
  const { dashboard_uuid: dashboardUuid } = params;
  const widgets = getMockDashboardLayouts()[dashboardUuid];
  if (!widgets) return notFound(`Unknown dashboard ${dashboardUuid}`);

  return envelope({
    dashboard_uuid: dashboardUuid,
    name: dashboardUuid,
    version: '1',
    filters: [],
    widgets,
  });
};

/** The card title this instance was published with, so the body is not titled "Widget". */
const titleForInstance = (instanceUuid) => {
  for (const widgets of Object.values(getMockDashboardLayouts())) {
    const found = widgets.find((w) => w.instance_uuid === instanceUuid);
    if (found) return found.config?.card?.title;
  }
  return undefined;
};

const handleWidgetData = (params = {}) => {
  const { instance_uuid: instanceUuid, scope_context: scopeContext } = params;
  const entityId = scopeContext?.entity_id ?? scopeContext?.entityId ?? null;
  return envelope(buildMockWidgetData(instanceUuid, entityId, titleForInstance(instanceUuid)), {
    entity_id: entityId,
  });
};

const handleTabsList = (params = {}) => {
  const { entity_type: entityType } = params;

  if (FORBIDDEN_TYPE_IDS.includes(entityType)) return forbidden(entityType);

  // A type with no extra tabs is an EMPTY LIST, not a 404: Overview alone is a
  // perfectly good profile, and the frontend adds that itself.
  const tabs = getMockProfileTabs()[entityType] ?? [];
  return envelope({ items: tabs }, { total_items: tabs.length });
};

/**
 * The Activity feed rides the same tab-data call as every other tab (CT-5527),
 * plus a `quick_filter`. Its rows are the feed's own shape, not the generic
 * entity card, and it is newest-first rather than alphabetical.
 */
const handleActivityList = (params = {}) => {
  const {
    entity_type: entityType,
    entity_id: entityId,
    tab_id: tabId,
    quick_filter: quickFilter = 'all',
    page = 1,
    page_size: pageSize = 10,
    order_direction: orderDirection = 'desc',
    filters = {},
    search = {},
    focus_event_id: focusEventId = null,
  } = params;

  if (ACTIVITY_FAILING_ENTITY_IDS.includes(entityId)) {
    return errorEnvelope(500, 'Mock activity failure.');
  }

  const matched = queryMockActivity({
    entityType,
    entityId,
    tabKey: `${entityType}/${tabId}`,
    quickFilter,
    filters,
    search,
    orderDirection,
  });
  // "Open in History" (CT-5724): the page that holds the event, when it is there.
  const focusIndex = focusEventId ? matched.findIndex((row) => row.id === focusEventId) : -1;
  const answeredPage = focusIndex >= 0 && pageSize > 0 ? Math.floor(focusIndex / pageSize) + 1 : page;
  const start = (answeredPage - 1) * pageSize;
  const items = matched.slice(start, start + pageSize).map(toApiActivityRow);
  const totalPages = pageSize > 0 ? Math.max(1, Math.ceil(matched.length / pageSize)) : 1;

  return envelope(
    {
      items,
      filters: activityFilterDefsFor(quickFilter),
      sorts: [{ value: 'occurred_at', label: 'Date' }],
      page: answeredPage,
      page_size: pageSize,
      total: matched.length,
    },
    {
      total_items: matched.length,
      total_pages: totalPages,
      current_page: answeredPage,
      per_page: pageSize,
      has_next: start + items.length < matched.length,
      has_previous: answeredPage > 1,
      ...(focusEventId ? { focus_event_id: focusEventId, focus_found: focusIndex >= 0 } : {}),
    },
  );
};

/**
 * Notes (CT-5528): the list rides the Activity call above; these are the note
 * resource itself. The rules live in `notes.mock.js` — each helper answers
 * `{ body, meta }` or `{ error }` and this only wraps it in an envelope.
 */
const asEnvelope = (result) =>
  result.error ? errorEnvelope(result.error.status, result.error.description) : envelope(result.body, result.meta);

const handleNoteGet = (params = {}) => asEnvelope(getMockNoteDetail(params.note_id));
const handleNoteCreate = (params = {}) => asEnvelope(createMockNote(params));
const handleNoteUpdate = (params = {}) => asEnvelope(updateMockNote(params));
const handleNoteCommentsList = (params = {}) => asEnvelope(listMockComments(params));
const handleNoteCommentsCreate = (params = {}) => asEnvelope(addMockComment(params));

/**
 * Traits (CT-5715). The rules and the `?mockIntelligence=` QA switch live in
 * `intelligence.mock.js`; this only wraps the answer in an envelope.
 */
const handleTraitsList = (params = {}) =>
  asEnvelope(
    queryMockTraits({
      entityType: params.entity_type,
      entityId: params.entity_id,
      quickFilter: params.quick_filter,
      page: params.page,
      pageSize: params.page_size,
      orderBy: params.order_by,
      orderDirection: params.order_direction,
      filters: params.filters,
      search: params.search,
    }),
  );
const handleTraitGet = (params = {}) =>
  asEnvelope(getMockTrait({ entityType: params.entity_type, entityId: params.entity_id, traitId: params.trait_id }));
const handleTraitReport = (params = {}) =>
  asEnvelope(reportMockTrait({ entityType: params.entity_type, entityId: params.entity_id, traitId: params.trait_id }));

/** Next Best Action (CT-5713): rules and the `?mockNba=` switch live in the mock module. */
const handleNbaList = (params = {}) =>
  asEnvelope(listMockNextBestActions({ entityType: params.entity_type, entityId: params.entity_id }));
const handleNbaWorkflowStart = (params = {}) =>
  asEnvelope(
    startMockNextBestActionWorkflow({ entityType: params.entity_type, entityId: params.entity_id, itemId: params.item_id }),
  );

/** Knowledge (CT-5717): the record's drive and the Truth summary line; `?mockKnowledge=` switch. */
const handleEntityDriveList = (params = {}) =>
  asEnvelope(
    listMockEntityDrive({
      entityType: params.entity_type,
      entityId: params.entity_id,
      folderId: params.folder_id,
      page: params.page,
      pageSize: params.page_size,
      orderDirection: params.order_direction,
      quickFilter: params.quick_filter,
      filters: params.filters,
      search: params.search,
    }),
  );
const handleDriveExtraction = (params = {}) =>
  asEnvelope(
    setMockDriveExtraction({
      entityType: params.entity_type,
      entityId: params.entity_id,
      folderId: params.folder_id,
      enabled: params.enabled,
    }),
  );
const handleDriveFolderCreate = (params = {}) =>
  asEnvelope(
    createMockDriveFolder({
      entityType: params.entity_type,
      entityId: params.entity_id,
      parentId: params.parent_id,
      name: params.name,
      description: params.description,
      extractClaims: params.extract_claims,
    }),
  );

/** Truth (CT-5719): reads the Knowledge switch; the rules live in the mock module. */
const truthArgs = (params = {}) => ({ entityType: params.entity_type, entityId: params.entity_id, factId: params.fact_id });
const handleTruthList = (params = {}) =>
  asEnvelope(
    listMockTruthFacts({
      entityType: params.entity_type,
      entityId: params.entity_id,
      quickFilter: params.quick_filter,
      filters: params.filters,
      search: params.search,
      orderBy: params.order_by,
      orderDirection: params.order_direction,
    }),
  );

/** Sandbox (CT-5718): reads the Knowledge switch; the rules live in the mock module. */
const sandboxArgs = (params = {}) => ({ entityType: params.entity_type, entityId: params.entity_id, claimId: params.claim_id });
const handleSandboxList = (params = {}) =>
  asEnvelope(
    listMockSandboxGroups({
      entityType: params.entity_type,
      entityId: params.entity_id,
      orderBy: params.order_by,
      orderDirection: params.order_direction,
      quickFilter: params.quick_filter,
      filters: params.filters,
      search: params.search,
    }),
  );

/** History (CT-5724): logged calls / meetings and From notes; `?mockHistory=` switch. */
const handleLogInteraction = (kind) => (params = {}) => asEnvelope(logMockInteraction({ ...params, kind }));
const handleNotesFeed = (params = {}) =>
  asEnvelope(
    listMockNotesFeed({
      entityType: params.entity_type,
      entityId: params.entity_id,
      quickFilter: params.quick_filter,
      search: params.search,
    }),
  );

/** Tasks (CT-5722): the rules and the `?mockTasks=` switch live in the mock module. */
const handleTasksList = (params = {}) =>
  asEnvelope(
    listMockTasks({
      entityType: params.entity_type,
      entityId: params.entity_id,
      filters: params.filters,
      search: params.search,
    }),
  );
const handleTaskMutation = (mutation) => (params = {}) =>
  asEnvelope(
    runMockTaskMutation({ entityType: params.entity_type, entityId: params.entity_id, taskId: params.task_id, mutation }),
  );

/** How to approach (CT-5714): derived from the Intelligence seeds; `?mockApproach=` switch. */
const handleApproachGet = (params = {}) =>
  asEnvelope(getMockHowToApproach({ entityType: params.entity_type, entityId: params.entity_id }));

/** Insights (CT-5716): same mock module and QA switch as the traits. */
const handleInsightsList = (params = {}) =>
  asEnvelope(
    queryMockInsights({
      entityType: params.entity_type,
      entityId: params.entity_id,
      quickFilter: params.quick_filter,
      orderBy: params.order_by,
      orderDirection: params.order_direction,
      filters: params.filters,
      search: params.search,
    }),
  );
const handleInsightGet = (params = {}) =>
  asEnvelope(getMockInsight({ entityType: params.entity_type, entityId: params.entity_id, insightId: params.insight_id }));
const handleInsightReport = (params = {}) =>
  asEnvelope(
    reportMockInsight({ entityType: params.entity_type, entityId: params.entity_id, insightId: params.insight_id }),
  );

/** The drive an attachment is picked from: vaults, then their folders and documents. */
const handleDrivesList = () => envelope({ items: getMockDrive().drives });
const handleDriveRoot = ({ vault_uuid: vaultUuid } = {}) =>
  envelope(getMockDrive().root[vaultUuid] ?? { folders: [], documents: [] });
const handleDriveFolder = ({ folder_uuid: folderUuid } = {}) =>
  envelope(getMockDrive().folders[folderUuid] ?? { folders: [], documents: [] });

const handleTabDataList = (params = {}) => {
  const {
    entity_type: entityType,
    tab_id: tabId,
    page = 1,
    page_size: pageSize = 10,
    order_by: orderBy = 'title',
    order_direction: orderDirection = 'asc',
    filters = {},
    search = {},
  } = params;

  if (FORBIDDEN_TYPE_IDS.includes(entityType)) return forbidden(entityType);

  if (hasMockActivity(`${entityType}/${tabId}`)) return handleActivityList(params);

  const tab = getMockTabData()[`${entityType}/${tabId}`];
  if (!tab) return notFound(`No data for tab ${tabId} on ${entityType}`);

  const needle = (search.query ?? '').trim().toLowerCase();
  const matchesSearch = (row) =>
    !needle ||
    `${row.title ?? ''} ${row.description ?? ''}`.toLowerCase().includes(needle);

  // Several values of ONE filter are OR; two different filters are AND. That is
  // what a chip-based toolbar means when a user picks Call and Note under Type
  // and then narrows by Author.
  const matchesFilters = (row) =>
    Object.entries(filters).every(([filterId, values]) => {
      if (!values?.length) return true;
      return values.includes(row.filter_values?.[filterId]);
    });

  // `title` is the one field the normalized row contract guarantees, so it is
  // the one sort every tab can offer; anything beyond it reads a filter value,
  // which is the only other thing a row is known to carry.
  const sortValue = (row) =>
    orderBy === 'title' ? (row.title ?? '') : (row.filter_values?.[orderBy] ?? '');

  const matched = tab.items
    .filter(matchesSearch)
    .filter(matchesFilters)
    .sort((a, b) => {
      const result = sortValue(a).localeCompare(sortValue(b), undefined, { numeric: true });
      return orderDirection === 'desc' ? -result : result;
    });
  const start = (page - 1) * pageSize;
  const items = matched.slice(start, start + pageSize);
  const totalPages = pageSize > 0 ? Math.max(1, Math.ceil(matched.length / pageSize)) : 1;

  return envelope(
    {
      items,
      filters: tab.filters,
      sorts: tab.sorts ?? [{ value: 'title', label: 'Name' }],
      page,
      page_size: pageSize,
      total: matched.length,
    },
    {
      total_items: matched.length,
      total_pages: totalPages,
      current_page: page,
      per_page: pageSize,
      has_next: start + items.length < matched.length,
      has_previous: page > 1,
    },
  );
};

/**
 * Create flow (CT-5525).
 *
 * Which types can be created here, and what identifies a duplicate. Employees
 * are AIMS OS members, not records a user types in, so they are absent: asking
 * to create one is a 403 like any other action the caller may not take.
 */
const CREATABLE = {
  customers: {
    keyField: 'email',
    required: ['full_name', 'email', 'location'],
    titleOf: (values) => values.full_name,
  },
  companies: {
    keyField: 'domain',
    required: ['name', 'domain'],
    titleOf: (values) => values.name,
  },
};

const normalizeEmail = (value) => (value ?? '').trim().toLowerCase();

/** `https://www.Riverbend.example/about?x=1` and `riverbend.example` are one domain. */
const normalizeDomain = (value) =>
  (value ?? '')
    .trim()
    .toLowerCase()
    .replace(/^[a-z][a-z0-9+.-]*:\/\//, '')
    .replace(/^www\./, '')
    .split(/[/?#]/)[0];

/** A subdomain of a known domain is the same company; a longer name that merely ends alike is not. */
const sameDomain = (candidate, known) =>
  Boolean(candidate) && Boolean(known) && (candidate === known || candidate.endsWith(`.${known}`));

const findExistingMatches = (entityType, key = {}) => {
  const rows = getMockEntities()[entityType] ?? [];
  if (entityType === 'customers') {
    const email = normalizeEmail(key.email);
    return email ? rows.filter((row) => normalizeEmail(row.email) === email) : [];
  }
  if (entityType === 'companies') {
    const domain = normalizeDomain(key.domain);
    return domain ? rows.filter((row) => sameDomain(domain, normalizeDomain(row.domain))) : [];
  }
  return [];
};

const existsBody = (matches) => ({
  exists: matches.length > 0,
  matches: matches.map((row) => ({
    entity_id: row.entity_id,
    title: row.title,
    entity_type: row.entity_type,
  })),
});

const handleEntityExists = (params = {}) => {
  const { entity_type: entityType, key } = params;

  if (FORBIDDEN_TYPE_IDS.includes(entityType)) return forbidden(entityType);
  if (!getMockEntities()[entityType]) return notFound(`Unknown Unified Profile type ${entityType}`);

  return envelope(existsBody(findExistingMatches(entityType, key)));
};

const buildCreatedRow = (entityType, values, id) => {
  const base = {
    entity_id: id,
    visual: null,
    entity_type: entityType,
    last_interaction: MOCK_NOW_ISO,
    owner_id: values.owner_id ?? null,
  };
  if (entityType === 'customers') {
    return {
      ...base,
      title: values.full_name.trim(),
      description: 'Created from the Records list.',
      badges: [{ label: 'Active', variant: 'success' }],
      meta: [{ label: 'Location', value: values.location }],
      type_tag: 'Customer',
      email: normalizeEmail(values.email),
      phones: values.phones ?? [],
    };
  }
  return {
    ...base,
    title: values.name.trim(),
    description: [values.industry, values.city].filter(Boolean).join(' · ') || 'Created from the Records list.',
    badges: [{ label: 'Prospect', variant: 'warning' }],
    meta: [
      ...(values.industry ? [{ label: 'Industry', value: values.industry }] : []),
      ...(values.city ? [{ label: 'City', value: values.city }] : []),
    ],
    type_tag: 'Company',
    domain: normalizeDomain(values.domain),
  };
};

const handleEntityCreate = (params = {}) => {
  const { entity_type: entityType, values = {} } = params;

  if (FORBIDDEN_TYPE_IDS.includes(entityType)) return forbidden(entityType);
  const seed = getMockEntities();
  if (!seed[entityType]) return notFound(`Unknown Unified Profile type ${entityType}`);

  const rules = CREATABLE[entityType];
  if (!rules) return errorEnvelope(403, `${entityType} records cannot be created here`);

  const missing = rules.required.filter((field) => !String(values[field] ?? '').trim());
  if (missing.length > 0) return errorEnvelope(422, `Missing required values: ${missing.join(', ')}`);

  const matches = findExistingMatches(entityType, { [rules.keyField]: values[rules.keyField] });
  if (matches.length > 0) {
    return {
      status_code: 409,
      body: existsBody(matches),
      msg_code: [{ code: 'conflict', description: `A ${entityType} record with this ${rules.keyField} already exists` }],
      meta: {},
    };
  }

  const prefix = entityType === 'customers' ? 'c' : 'co';
  const id = `${prefix}-${seed[entityType].length + 1}`;
  const row = buildCreatedRow(entityType, values, id);
  appendMockEntity(entityType, row);
  appendMockEntityHeader(entityType, row);
  bumpMockProfileTypeCount(entityType);

  return envelope({ entity_id: id, entity_type: entityType });
};

const handleRecordOptions = () =>
  envelope({ locations: MOCK_LOCATIONS, industries: MOCK_INDUSTRIES, cities: MOCK_CITIES });

const handleOwnerList = (params = {}) => {
  const { page = 1, page_size: pageSize = 20, search = {} } = params;

  const needle = (search.query ?? '').trim().toLowerCase();
  const matched = needle
    ? MOCK_OWNERS.filter((owner) => `${owner.name} ${owner.email}`.toLowerCase().includes(needle))
    : MOCK_OWNERS;

  const start = (page - 1) * pageSize;
  const items = matched.slice(start, start + pageSize);
  const totalPages = pageSize > 0 ? Math.max(1, Math.ceil(matched.length / pageSize)) : 1;

  return envelope(
    { items, page, page_size: pageSize, total: matched.length },
    {
      total_items: matched.length,
      total_pages: totalPages,
      current_page: page,
      per_page: pageSize,
      has_next: start + items.length < matched.length,
      has_previous: page > 1,
    },
  );
};

async function mockRequest(path, payload) {
  await delay(MOCK_LATENCY_MS);

  const shouldFail = forceFailureOverride ?? ENV_FORCE_ERROR;
  if (shouldFail) {
    return errorEnvelope(500, 'Mock forced failure.');
  }

  switch (path) {
    case '/v0/profile/types/list':
      return handleTypeList(payload);
    case '/v0/profile/entities/list':
      return handleEntityList(payload);
    case '/v0/profile/entities/get':
      return handleEntityGet(payload);
    case '/v0/profile/entities/exists':
      return handleEntityExists(payload);
    case '/v0/profile/entities/create':
      return handleEntityCreate(payload);
    case '/v0/profile/records/options':
      return handleRecordOptions(payload);
    case '/v0/profile/owners/list':
      return handleOwnerList(payload);
    case '/v0/profile/dashboards/list':
      return handleDashboardList(payload);
    case '/v0/profile/dashboards/layout':
      return handleDashboardLayout(payload);
    case '/v0/profile/widgets/data':
      return handleWidgetData(payload);
    case '/v0/profile/tabs/list':
      return handleTabsList(payload);
    case '/v0/profile/tabs/data/list':
      return handleTabDataList(payload);
    case '/v0/profile/notes/get':
      return handleNoteGet(payload);
    case '/v0/profile/notes/create':
      return handleNoteCreate(payload);
    case '/v0/profile/notes/update':
      return handleNoteUpdate(payload);
    case '/v0/profile/notes/comments/list':
      return handleNoteCommentsList(payload);
    case '/v0/profile/notes/comments/create':
      return handleNoteCommentsCreate(payload);
    case '/v0/profile/traits/list':
      return handleTraitsList(payload);
    case '/v0/profile/traits/get':
      return handleTraitGet(payload);
    case '/v0/profile/traits/report':
      return handleTraitReport(payload);
    case '/v0/profile/entity-drive/list':
      return handleEntityDriveList(payload);
    case '/v0/profile/entity-drive/extraction/set':
      return handleDriveExtraction(payload);
    case '/v0/profile/entity-drive/folders/create':
      return handleDriveFolderCreate(payload);
    case '/v0/profile/sandbox/groups/list':
      return handleSandboxList(payload);
    case '/v0/profile/sandbox/claims/confirm':
      return asEnvelope(confirmMockClaim(sandboxArgs(payload)));
    case '/v0/profile/sandbox/claims/send-confirmation':
      return asEnvelope(sendMockClaimForConfirmation(sandboxArgs(payload)));
    case '/v0/profile/sandbox/conflicts/get':
      return asEnvelope(getMockConflict(sandboxArgs(payload)));
    case '/v0/profile/sandbox/conflicts/resolve':
      return asEnvelope(resolveMockConflict({ ...sandboxArgs(payload), choice: payload?.choice }));
    case '/v0/profile/truth/summary':
      return asEnvelope(getMockTruthSummary(payload));
    case '/v0/profile/truth/list':
      return handleTruthList(payload);
    case '/v0/profile/truth/facts/get':
      return asEnvelope(getMockTruthFact(truthArgs(payload)));
    case '/v0/profile/truth/facts/request-change':
      return asEnvelope(requestMockFactChange(truthArgs(payload)));
    case '/v0/profile/truth/facts/report':
      return asEnvelope(reportMockTruthFact(truthArgs(payload)));
    case '/v0/profile/interactions/log-call':
      return handleLogInteraction('call')(payload);
    case '/v0/profile/interactions/log-meeting':
      return handleLogInteraction('meeting')(payload);
    case '/v0/profile/notes-feed/list':
      return handleNotesFeed(payload);
    case '/v0/profile/tasks/list':
      return handleTasksList(payload);
    case '/v0/profile/tasks/take-over':
      return handleTaskMutation('take_over')(payload);
    case '/v0/profile/tasks/complete':
      return handleTaskMutation('complete')(payload);
    case '/v0/profile/tasks/snooze':
      return handleTaskMutation('snooze')(payload);
    case '/v0/profile/tasks/dismiss':
      return handleTaskMutation('dismiss')(payload);
    case '/v0/profile/approach/get':
      return handleApproachGet(payload);
    case '/v0/profile/nba/list':
      return handleNbaList(payload);
    case '/v0/profile/nba/workflow/start':
      return handleNbaWorkflowStart(payload);
    case '/v0/profile/insights/list':
      return handleInsightsList(payload);
    case '/v0/profile/insights/get':
      return handleInsightGet(payload);
    case '/v0/profile/insights/report':
      return handleInsightReport(payload);
    case '/v0/profile/drives/list':
      return handleDrivesList();
    case '/v0/profile/drives/root':
      return handleDriveRoot(payload);
    case '/v0/profile/drives/folder':
      return handleDriveFolder(payload);
    default:
      return notFound(`Unknown path ${path}`);
  }
}

/**
 * The single transport seam for this domain.
 *
 * Mock mode resolves the same envelope the endpoint will return; real mode
 * posts through `apiUnifiedProfiles`. When the endpoints land, delete the mock
 * modules and flip `VITE_UNIFIED_PROFILES_USE_MOCKS` — no service, hook,
 * mapper or component changes.
 */
export async function request(path, payload) {
  if (USE_MOCKS) {
    return mockRequest(path, payload);
  }
  return apiUnifiedProfiles.post(path, payload);
}
